import { z } from 'zod';
import {
  CASAS_DINHEIRO,
  CASAS_PERCENTUAL,
  compararPercentual,
  desescalar,
  escalar,
  somarDinheiro,
} from './aritmetica.ts';
import { dataSchema, dinheiroSchema, uuidSchema } from './comum.ts';
import { situacaoDoTitulo, situacaoTituloSchema } from './titulos.ts';

/**
 * Relatório de contas a pagar (`PAG·03`).
 *
 * Os títulos a pagar **quebrados por empresa** e em ordem de **vencimento**,
 * com subtotal de valor e de saldo a cada data de vencimento — a pergunta "o
 * que vence em cada dia, e quanto disso ainda se deve", impressa. A listagem
 * (`PAG·01`) responde a mesma pergunta título a título, paginada e sem soma; o
 * painel (`PAG·02`) responde de relance, sem título nenhum. Este é o papel.
 *
 * ## De onde vem cada linha
 *
 * De `contas_pagar`, e só dela: um título, uma linha. As três origens entram —
 * nota de compra, contrato de pagamento e lançamento manual —, porque a
 * pergunta é sobre a dívida, e não sobre o documento que a criou.
 *
 * ## As duas colunas que não são do título
 *
 * - **Emissão.** `contas_pagar` não tem data de emissão: tem `data_lancamento`,
 *   o dia em que o título entrou no sistema. Para o título que nasceu de nota,
 *   a emissão é a da **nota fiscal** (`notas_compra.data_emissao`), que é a
 *   data impressa no papel que o fornecedor mandou; para os outros, que não têm
 *   documento fiscal, é o próprio lançamento. O filtro de emissão lê a mesma
 *   expressão que a coluna mostra — filtrar por uma data e exibir outra faria o
 *   título aparecer "fora do período" que o usuário escolheu.
 * - **Conta contábil.** O título não tem uma conta: tem um **rateio** de contas
 *   por índice (`contas_pagar_contas_contabeis`), porque a parcela de uma nota
 *   cobre itens de naturezas diferentes. A linha mostra a **principal** — a de
 *   maior índice — e quantas mais existem ("+2"). O resumo por conta contábil,
 *   ao contrário, **divide** título e saldo pelo índice de cada conta, e por
 *   isso fecha exatamente no total do relatório. Decisão do Adriano,
 *   25/09/2026.
 *
 * ## O que se agrega onde
 *
 * O servidor devolve as empresas, os vencimentos e o resumo **já montados**:
 * as somas são dinheiro, e dinheiro se soma aqui, com `BigInt`. A tela só
 * decide se mostra o resumo — e isso não muda o que se soma, então não é
 * parâmetro da rota.
 */

/* -------------------------------------------------------------------------- */
/* Teto                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Teto de títulos por consulta.
 *
 * O relatório não é paginado: os subtotais por vencimento, o resumo e o PDF
 * precisam do conjunto inteiro, e uma página 2 imprimiria um dia de vencimento
 * pela metade. O teto **recusa** em vez de truncar — relatório cortado em
 * silêncio soma menos do que se deve, e ninguém percebe.
 */
export const LIMITE_TITULOS_RELATORIO_CONTAS_PAGAR = 5000;

/* -------------------------------------------------------------------------- */
/* Consulta                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Filtros do relatório. **Todos opcionais**, e a ausência é "sem recorte".
 *
 * - `empresaId` é a empresa **devedora** (`contas_pagar.empresa_id`).
 * - Os dois períodos são inclusivos nas duas pontas. A emissão é a da nota
 *   quando o título veio dela, e o lançamento nos outros (ver o cabeçalho).
 * - `situacao` segue o **recorte da listagem**, e não o rótulo: `EM_ABERTO` traz
 *   tudo com saldo, inclusive o baixado em parte; `PARCIAL` isola só esses;
 *   `QUITADO` é saldo zero. É o mesmo `filtroDeSituacao` do repositório do
 *   contas a pagar — o relatório e a listagem com o mesmo filtro contam os
 *   mesmos títulos.
 *
 * Estrito: parâmetro desconhecido é erro, e não silêncio (ver `query-estrita`).
 */
export const relatorioContasPagarQuerySchema = z
  .object({
    empresaId: uuidSchema.optional(),
    fornecedorId: uuidSchema.optional(),
    emissaoDe: dataSchema.optional(),
    emissaoAte: dataSchema.optional(),
    vencimentoDe: dataSchema.optional(),
    vencimentoAte: dataSchema.optional(),
    situacao: situacaoTituloSchema.optional(),
  })
  .strict();

export type RelatorioContasPagarQuery = z.infer<typeof relatorioContasPagarQuerySchema>;
/** O que o cliente manda. Igual à saída hoje — não há padrão —, e separado por simetria com CTR·05. */
export type RelatorioContasPagarQueryEntrada = z.input<typeof relatorioContasPagarQuerySchema>;

/* -------------------------------------------------------------------------- */
/* Resposta                                                                   */
/* -------------------------------------------------------------------------- */

/** Um título do relatório. */
export const linhaRelatorioContasPagarSchema = z.object({
  contaPagarId: uuidSchema,
  numeroDocumento: z.string(),
  parcela: z.number().int().positive(),
  totalParcelas: z.number().int().positive(),
  /** Emissão da nota, ou o lançamento do título sem nota. */
  dataEmissao: dataSchema,
  dataVencimento: dataSchema,
  valor: dinheiroSchema,
  valorSaldo: dinheiroSchema,
  situacao: situacaoTituloSchema,
  fornecedorId: uuidSchema,
  fornecedorNome: z.string(),
  /** A conta de maior índice. `null` só no título sem classificação nenhuma. */
  contaContabilId: uuidSchema.nullable(),
  contaContabilCodigo: z.string().nullable(),
  contaContabilDescricao: z.string().nullable(),
  /** Quantas contas o título tem **além** da principal — o "+2" da coluna. */
  outrasContasContabeis: z.number().int().nonnegative(),
});

export type LinhaRelatorioContasPagar = z.infer<typeof linhaRelatorioContasPagarSchema>;

/** Soma de um bloco: um vencimento, uma empresa ou o relatório inteiro. */
export const totaisRelatorioContasPagarSchema = z.object({
  valor: dinheiroSchema,
  saldo: dinheiroSchema,
  titulos: z.number().int().nonnegative(),
});

export type TotaisRelatorioContasPagar = z.infer<typeof totaisRelatorioContasPagarSchema>;

/** Os títulos de uma data de vencimento, com o subtotal dela. */
export const vencimentoRelatorioContasPagarSchema = z.object({
  dataVencimento: dataSchema,
  titulos: z.array(linhaRelatorioContasPagarSchema),
  totais: totaisRelatorioContasPagarSchema,
});

export type VencimentoRelatorioContasPagar = z.infer<typeof vencimentoRelatorioContasPagarSchema>;

/** A quebra por empresa. */
export const empresaRelatorioContasPagarSchema = z.object({
  empresaId: uuidSchema,
  empresaNome: z.string(),
  vencimentos: z.array(vencimentoRelatorioContasPagarSchema),
  totais: totaisRelatorioContasPagarSchema,
});

export type EmpresaRelatorioContasPagar = z.infer<typeof empresaRelatorioContasPagarSchema>;

/** Uma linha do resumo por fornecedor. */
export const fornecedorResumoContasPagarSchema = z.object({
  fornecedorId: uuidSchema,
  fornecedorNome: z.string(),
  valor: dinheiroSchema,
  saldo: dinheiroSchema,
  titulos: z.number().int().nonnegative(),
});

export type FornecedorResumoContasPagar = z.infer<typeof fornecedorResumoContasPagarSchema>;

/**
 * Uma linha do resumo por conta contábil — valor e saldo **rateados** pelo
 * índice de cada título. `titulos` conta os títulos que têm fatia na conta; a
 * soma da coluna passa do total de títulos quando algum tem mais de uma conta.
 */
export const contaResumoContasPagarSchema = z.object({
  /** `null` agrupa o título sem classificação — não deveria existir, mas aparece se existir. */
  contaContabilId: uuidSchema.nullable(),
  contaContabilCodigo: z.string().nullable(),
  contaContabilDescricao: z.string().nullable(),
  valor: dinheiroSchema,
  saldo: dinheiroSchema,
  titulos: z.number().int().nonnegative(),
});

export type ContaResumoContasPagar = z.infer<typeof contaResumoContasPagarSchema>;

export const resumoRelatorioContasPagarSchema = z.object({
  porFornecedor: z.array(fornecedorResumoContasPagarSchema),
  porContaContabil: z.array(contaResumoContasPagarSchema),
});

export type ResumoRelatorioContasPagar = z.infer<typeof resumoRelatorioContasPagarSchema>;

/**
 * O relatório inteiro.
 *
 * O filtro volta **resolvido** — os nomes da empresa e do fornecedor —, porque o
 * PDF imprime o cabeçalho e resolver os nomes no cliente custaria duas consultas
 * a mais. As datas voltam como vieram, `null` incluído: é o que distingue "todo
 * o histórico" de um período escolhido.
 *
 * Só aparecem as empresas **com** título no recorte.
 */
export const relatorioContasPagarSchema = z.object({
  empresaId: uuidSchema.nullable(),
  empresaNome: z.string().nullable(),
  fornecedorId: uuidSchema.nullable(),
  fornecedorNome: z.string().nullable(),
  emissaoDe: dataSchema.nullable(),
  emissaoAte: dataSchema.nullable(),
  vencimentoDe: dataSchema.nullable(),
  vencimentoAte: dataSchema.nullable(),
  situacao: situacaoTituloSchema.nullable(),
  empresas: z.array(empresaRelatorioContasPagarSchema),
  totais: totaisRelatorioContasPagarSchema,
  resumo: resumoRelatorioContasPagarSchema,
});

export type RelatorioContasPagar = z.infer<typeof relatorioContasPagarSchema>;

/* -------------------------------------------------------------------------- */
/* Montagem — do que o banco leu ao relatório                                 */
/* -------------------------------------------------------------------------- */

/** Um título como o repositório o devolve, com a empresa e o fornecedor ao lado. */
export interface TituloBrutoContasPagar {
  contaPagarId: string;
  empresaId: string;
  empresaNome: string;
  fornecedorId: string;
  fornecedorNome: string;
  numeroDocumento: string;
  parcela: number;
  totalParcelas: number;
  dataEmissao: string;
  dataVencimento: string;
  valor: string;
  valorSaldo: string;
}

/** Uma fatia da classificação contábil de um título. */
export interface ClassificacaoBrutaContasPagar {
  contaPagarId: string;
  contaContabilId: string;
  contaContabilCodigo: string | null;
  contaContabilDescricao: string | null;
  indiceRateio: string;
}

const COMPARAR_TEXTO = new Intl.Collator('pt-BR', { sensitivity: 'base', numeric: true });

/** Código de conta em ordem de plano: "1.2.10" depois de "1.2.9". Sem código, no fim. */
function compararCodigo(a: string | null, b: string | null): number {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return COMPARAR_TEXTO.compare(a, b);
}

/**
 * Divide um valor em dinheiro pelos índices, **sem perder nem criar centavo**.
 *
 * Maior resto: cada fatia recebe o piso de `valor × índice ÷ 100`, e os
 * centavos que sobram vão, um a um, às fatias de maior resto (empate: a que vem
 * primeiro). A soma das fatias é o valor exato quando os índices somam 100%, e
 * é o que faz o resumo por conta contábil fechar no total do relatório — o
 * arredondamento fatia a fatia erraria um centavo a cada três títulos.
 *
 * Índices que somam menos de 100% distribuem só a parte deles (o que sobra não
 * é de conta nenhuma); mais de 100% não acontece — a classificação do título
 * fecha em 100% por regra.
 */
export function ratearValorPorIndice(valor: string, indices: readonly string[]): string[] {
  if (indices.length === 0) return [];

  const centavos = escalar(valor, CASAS_DINHEIRO);
  const cem = escalar('100', CASAS_PERCENTUAL);
  const pesos = indices.map((indice) => escalar(indice, CASAS_PERCENTUAL));
  const somaDosPesos = pesos.reduce((soma, peso) => soma + peso, 0n);

  // O alvo é a parte do valor que os índices cobrem: tudo, quando somam 100%.
  const alvo = somaDosPesos >= cem ? centavos : (centavos * somaDosPesos) / cem;

  const brutos = pesos.map((peso) => centavos * peso);
  const pisos = brutos.map((bruto) => bruto / cem);
  let sobra = alvo - pisos.reduce((soma, piso) => soma + piso, 0n);

  const porResto = brutos
    .map((bruto, indice) => ({ indice, resto: bruto % cem }))
    .sort((a, b) => (b.resto > a.resto ? 1 : b.resto < a.resto ? -1 : a.indice - b.indice));

  const fatias = [...pisos];
  for (const { indice } of porResto) {
    if (sobra <= 0n) break;
    fatias[indice] = (fatias[indice] as bigint) + 1n;
    sobra -= 1n;
  }

  return fatias.map((fatia) => desescalar(fatia, CASAS_DINHEIRO));
}

/**
 * A conta que representa o título na linha: a de **maior índice**.
 *
 * Empate de índice — 50% e 50% — desempata pelo código, para a mesma nota sair
 * sempre com a mesma conta, na tela e no PDF.
 */
export function contaPrincipalDoTitulo(
  classificacoes: readonly ClassificacaoBrutaContasPagar[],
): ClassificacaoBrutaContasPagar | null {
  const [principal = null] = [...classificacoes].sort(
    (a, b) =>
      compararPercentual(b.indiceRateio, a.indiceRateio) ||
      compararCodigo(a.contaContabilCodigo, b.contaContabilCodigo),
  );
  return principal;
}

function totaisDe(
  titulos: readonly { valor: string; valorSaldo: string }[],
): TotaisRelatorioContasPagar {
  return {
    valor: somarDinheiro(titulos.map((t) => t.valor)),
    saldo: somarDinheiro(titulos.map((t) => t.valorSaldo)),
    titulos: titulos.length,
  };
}

function paraLinha(
  titulo: TituloBrutoContasPagar,
  classificacoes: readonly ClassificacaoBrutaContasPagar[],
): LinhaRelatorioContasPagar {
  const principal = contaPrincipalDoTitulo(classificacoes);

  return {
    contaPagarId: titulo.contaPagarId,
    numeroDocumento: titulo.numeroDocumento,
    parcela: titulo.parcela,
    totalParcelas: titulo.totalParcelas,
    dataEmissao: titulo.dataEmissao,
    dataVencimento: titulo.dataVencimento,
    valor: titulo.valor,
    valorSaldo: titulo.valorSaldo,
    situacao: situacaoDoTitulo(titulo.valor, titulo.valorSaldo),
    fornecedorId: titulo.fornecedorId,
    fornecedorNome: titulo.fornecedorNome,
    contaContabilId: principal?.contaContabilId ?? null,
    contaContabilCodigo: principal?.contaContabilCodigo ?? null,
    contaContabilDescricao: principal?.contaContabilDescricao ?? null,
    outrasContasContabeis: Math.max(classificacoes.length - 1, 0),
  };
}

/**
 * A ordem dentro de um vencimento: fornecedor, documento e parcela.
 *
 * O vencimento já é a quebra; dentro dele, o fornecedor agrupa o que se paga
 * junto, e o documento com a parcela deixa as três parcelas de uma nota em
 * sequência, na ordem em que o papel as numera.
 */
function compararTitulos(a: LinhaRelatorioContasPagar, b: LinhaRelatorioContasPagar): number {
  return (
    COMPARAR_TEXTO.compare(a.fornecedorNome, b.fornecedorNome) ||
    COMPARAR_TEXTO.compare(a.numeroDocumento, b.numeroDocumento) ||
    a.parcela - b.parcela ||
    a.contaPagarId.localeCompare(b.contaPagarId)
  );
}

function vencimentosDe(
  linhas: readonly LinhaRelatorioContasPagar[],
): VencimentoRelatorioContasPagar[] {
  const porData = new Map<string, LinhaRelatorioContasPagar[]>();
  for (const linha of linhas) {
    const doDia = porData.get(linha.dataVencimento) ?? [];
    doDia.push(linha);
    porData.set(linha.dataVencimento, doDia);
  }

  return [...porData]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dataVencimento, titulos]) => {
      const ordenados = [...titulos].sort(compararTitulos);
      return { dataVencimento, titulos: ordenados, totais: totaisDe(ordenados) };
    });
}

interface Acumulado {
  valores: string[];
  saldos: string[];
  titulos: number;
}

function acumular(mapa: Map<string, Acumulado>, chave: string, valor: string, saldo: string): void {
  const atual = mapa.get(chave) ?? { valores: [], saldos: [], titulos: 0 };
  atual.valores.push(valor);
  atual.saldos.push(saldo);
  atual.titulos += 1;
  mapa.set(chave, atual);
}

/** O resumo por fornecedor: o título inteiro vai ao fornecedor dele. */
function resumoPorFornecedor(
  titulos: readonly TituloBrutoContasPagar[],
): FornecedorResumoContasPagar[] {
  const acumulados = new Map<string, Acumulado>();
  const nomes = new Map<string, string>();

  for (const titulo of titulos) {
    acumular(acumulados, titulo.fornecedorId, titulo.valor, titulo.valorSaldo);
    nomes.set(titulo.fornecedorId, titulo.fornecedorNome);
  }

  return [...acumulados]
    .map(([fornecedorId, a]) => ({
      fornecedorId,
      fornecedorNome: nomes.get(fornecedorId) ?? '',
      valor: somarDinheiro(a.valores),
      saldo: somarDinheiro(a.saldos),
      titulos: a.titulos,
    }))
    .sort((a, b) => COMPARAR_TEXTO.compare(a.fornecedorNome, b.fornecedorNome));
}

const SEM_CONTA = '';

/**
 * O resumo por conta contábil: valor e saldo de cada título **divididos** pelo
 * índice de cada conta dele, com `ratearValorPorIndice`.
 *
 * Valor e saldo são rateados cada um por si — ratear o valor e derivar o saldo
 * pela proporção arredondaria duas vezes. O título sem classificação vai inteiro
 * para a linha sem conta.
 */
function resumoPorConta(
  titulos: readonly TituloBrutoContasPagar[],
  classificacoesPorTitulo: ReadonlyMap<string, readonly ClassificacaoBrutaContasPagar[]>,
): ContaResumoContasPagar[] {
  const acumulados = new Map<string, Acumulado>();
  const contas = new Map<string, ClassificacaoBrutaContasPagar | null>();

  for (const titulo of titulos) {
    const fatias = classificacoesPorTitulo.get(titulo.contaPagarId) ?? [];

    if (fatias.length === 0) {
      acumular(acumulados, SEM_CONTA, titulo.valor, titulo.valorSaldo);
      contas.set(SEM_CONTA, null);
      continue;
    }

    const indices = fatias.map((fatia) => fatia.indiceRateio);
    const valores = ratearValorPorIndice(titulo.valor, indices);
    const saldos = ratearValorPorIndice(titulo.valorSaldo, indices);

    fatias.forEach((fatia, indice) => {
      acumular(
        acumulados,
        fatia.contaContabilId,
        valores[indice] as string,
        saldos[indice] as string,
      );
      contas.set(fatia.contaContabilId, fatia);
    });
  }

  return [...acumulados]
    .map(([chave, a]) => {
      const conta = contas.get(chave) ?? null;
      return {
        contaContabilId: conta?.contaContabilId ?? null,
        contaContabilCodigo: conta?.contaContabilCodigo ?? null,
        contaContabilDescricao: conta?.contaContabilDescricao ?? null,
        valor: somarDinheiro(a.valores),
        saldo: somarDinheiro(a.saldos),
        titulos: a.titulos,
      };
    })
    .sort(
      (a, b) =>
        (a.contaContabilId === null ? 1 : 0) - (b.contaContabilId === null ? 1 : 0) ||
        compararCodigo(a.contaContabilCodigo, b.contaContabilCodigo) ||
        COMPARAR_TEXTO.compare(a.contaContabilDescricao ?? '', b.contaContabilDescricao ?? ''),
    );
}

/**
 * Monta o relatório: quebra por empresa, agrupa por vencimento, fecha os
 * subtotais e o resumo.
 *
 * Vive no contrato, e não no service, porque é regra pura e testável sem banco:
 * as somas e a divisão por conta contábil são o ponto em que o relatório pode
 * perder um centavo, e é aí que o teste precisa estar.
 *
 * Empresas em ordem alfabética; vencimentos do mais antigo ao mais novo.
 */
export function montarRelatorioContasPagar(
  titulos: readonly TituloBrutoContasPagar[],
  classificacoes: readonly ClassificacaoBrutaContasPagar[],
): Pick<RelatorioContasPagar, 'empresas' | 'totais' | 'resumo'> {
  const classificacoesPorTitulo = new Map<string, ClassificacaoBrutaContasPagar[]>();
  for (const fatia of classificacoes) {
    const doTitulo = classificacoesPorTitulo.get(fatia.contaPagarId) ?? [];
    doTitulo.push(fatia);
    classificacoesPorTitulo.set(fatia.contaPagarId, doTitulo);
  }

  const porEmpresa = new Map<string, { nome: string; linhas: LinhaRelatorioContasPagar[] }>();
  for (const titulo of titulos) {
    const empresa = porEmpresa.get(titulo.empresaId) ?? { nome: titulo.empresaNome, linhas: [] };
    empresa.linhas.push(paraLinha(titulo, classificacoesPorTitulo.get(titulo.contaPagarId) ?? []));
    porEmpresa.set(titulo.empresaId, empresa);
  }

  const empresas = [...porEmpresa]
    .map(([empresaId, { nome, linhas }]) => ({
      empresaId,
      empresaNome: nome,
      vencimentos: vencimentosDe(linhas),
      totais: totaisDe(linhas),
    }))
    .sort((a, b) => COMPARAR_TEXTO.compare(a.empresaNome, b.empresaNome));

  return {
    empresas,
    totais: totaisDe(titulos),
    resumo: {
      porFornecedor: resumoPorFornecedor(titulos),
      porContaContabil: resumoPorConta(titulos, classificacoesPorTitulo),
    },
  };
}
